import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import dayjs from 'dayjs'
import arReportService from '@api/v1/arReportService'
import { specializedAttachFormats } from '@api/v1/specializedReportService'
import reportJobService from '@api/v1/reportJobService'
import matterService from '@api/v1/matterService'
import ReportDataGrid from '@components/reports/ReportDataGrid'
import ReportGroupedGrid from '@components/reports/ReportGroupedGrid'
import { stableAliasKeys } from '@components/reports/aliasedGridColumns'
import { trackGridTransform } from '@/analytics/gridTransforms'
import SpecializedReportShell, { SAVED_RUN_CRITERIA_UNKNOWN } from '@components/reports/SpecializedReportShell'
import { arSavedCriteria } from '@components/reports/savedRunCriteria'
import SpecializedFilterBar from '@components/reports/SpecializedFilterBar'
import { objectField, dateField, choiceField, boolField } from '@components/reports/specializedFilterFields'
import ReportViewsMenu from '@components/reports/ReportViewsMenu'
import ControlCheckbox, { TOGGLE_ROW, TOGGLE_ROW_BAND } from '@components/common/ControlCheckbox'
import useSavedReportView from '@components/reports/useSavedReportView'
import { compareCellValues, exportFileName } from '@components/reports/reportGridUtils'
import { fmtDisplayDate, fmtDisplayDateTime } from '@utils/displayDate'
import usePreferencesStore from '@store/preferencesStore'
import useFirmName from '@hooks/useFirmName'
import useUsePosting from '@hooks/useUsePosting'
import useLatestRun from '@hooks/useLatestRun'
import { useReportExportView } from '@components/reports/reportExportViewContext'

const VIEW_MODES = [
  { id: 'Matter', label: 'By Matter' },
  { id: 'Client', label: 'By Client' },
  { id: 'Timekeeper', label: 'By Timekeeper' },
  { id: 'GroupByOriginator', label: 'Group by Originator' },
  { id: 'GroupByInvoice', label: 'Group by Invoice' },
]

const POSTED_OPTIONS = [
  { value: 'all', label: "Don't filter" },
  { value: 'posted', label: 'Posted' },
  { value: 'unposted', label: 'Unposted' },
]

/**
 * CC-50013 G2 — AR's filter fields.
 *
 * Built per render because two fields are conditional, exactly as before:
 * Timekeeper only exists in the Timekeeper view, and "Show details" is hidden in
 * Group-by-Invoice (that mode forces showDetails false).
 *
 * The wire is untouched: `filterPayload` below is still the single boundary and
 * still sends only the eight keys it always did — the *Name fields these specs
 * write are page-state-only, for the picker labels, and never reach the server.
 *
 * The As Of date is a SINGLE date, not a range, so it is `dateField` — an
 * aged-A/R balance is a point-in-time figure and a from/to would be meaningless.
 */
const buildFields = (viewMode) => [
  objectField({ key: 'orig', label: 'Originator', typeId: 1, idKey: 'origId', nameKey: 'origName' }),
  objectField({ key: 'client', label: 'Client', typeId: 3, idKey: 'companyId', nameKey: 'companyName' }),
  objectField({ key: 'matter', label: 'Matter', typeId: 1001, idKey: 'matterId', nameKey: 'matterName' }),
  ...(viewMode === 'Timekeeper'
    ? [objectField({ key: 'timekeeper', label: 'Timekeeper', typeId: 1, idKey: 'timekeeperId', nameKey: 'timekeeperName' })]
    : []),
  dateField({ key: 'asOf', label: 'As Of', valueKey: 'endDate' }),
  choiceField({
    key: 'posted', label: 'Posted', valueKey: 'postedFilter',
    options: POSTED_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
    // bool? tri-state: null = don't filter, NOT the same as false (CC-49752).
    encode: (v) => (v == null ? 'all' : v ? 'posted' : 'unposted'),
    decode: (s) => (s === 'all' ? null : s === 'posted'),
  }),
  ...(viewMode !== 'GroupByInvoice'
    ? [boolField({ key: 'showDetails', label: 'Show details', valueKey: 'showDetails' })]
    : []),
]

/*
 * Aged A/R's OWN display-control defaults, in one place — the page's half of the
 * grids' `GRID_DISPLAY_DEFAULTS`.
 *
 * These three controls are handed to the grid as `pageDisplayExtras`, and E4's
 * "Reset grid layout" now restores them through `onResetPageDisplayExtras`. Both
 * the `useState` initialisers below and that reset read this object, so the two
 * cannot drift the way the grids' own four literals did.
 *
 * `hideZero: true` and it must stay true here — this is the one default in the
 * report chrome that resets toward HIDING rows, so it is worth stating why that
 * is safe. It is CC-49344's shipped default (pinned by ARReport.test.jsx, "every
 * Aged A/R figure depends on it"), so it is what the page genuinely starts at,
 * and a reset that produced any other state would be inventing a third notion of
 * default.
 *
 * ⚠️ SUPERSEDED SAFETY ARGUMENT, corrected 2026-08-28. This comment used to say
 * the hidden rows have "every numeric column < half a cent: `totals` sums
 * `visibleRows`, so restoring the default adds zero to every aging bucket and to
 * the grand total. No figure moves — only the row count."
 *
 * The premise is true and the conclusion does not follow. `totals` does sum
 * `visibleRows` (:491-504) and the predicate at :484 keeps a row only if some
 * numeric column is `>= 0.005`. But "each row is under half a cent" is not
 * "zero": N hidden rows move a bucket by up to 0.005 * N, and on an A/R report
 * the near-zero rows are fully-paid matters, so N is not small. At 1,000 such
 * rows that is up to $5.00 — visible at the displayed precision. The direction
 * is downward, because restoring `hideZero: true` REMOVES those rows.
 *
 * So the honest statement is the bounded one: restoring the default cannot move
 * a bucket by more than half a cent per row it hides, and it moves it DOWN.
 * That is a real bound, not a zero. What actually justifies the default is the
 * first half of this comment — `true` is the state the page genuinely starts in,
 * and a reset producing anything else would invent a third notion of default.
 * Nothing else here may follow that precedent without its own argument, and no
 * future reader should cite "no figure moves" from this file.
 */
const AR_DISPLAY_DEFAULTS = Object.freeze({
  hideZero: true,
  hideInactive: false,
  practiceAreaFilter: '',
})

const TYPE_USERS = 1
const TYPE_COMPANIES = 3
const TYPE_MATTERS = 1001

const todayStr = () => dayjs().format('YYYY-MM-DD')

// Reset restores THESE, not the smart-search prefill: arriving via ?ei=&et= scopes
// the report to one matter/client, and Reset is how a user gets back out of that
// scope. Deliberately excludes the posted default, which the tenant-driven effect
// re-applies.
const makeDefaults = () => ({
  companyId: null, companyName: '',
  origId: null, origName: '',
  matterId: null, matterName: '',
  timekeeperId: null, timekeeperName: '',
  viewMode: 'Matter',
  postedFilter: null, // null | true | false
  showDetails: false,
  endDate: todayStr(),
})

// The wire object — the EIGHT keys AR has always sent. The four *Name fields are
// page-state only (picker labels) and must never appear here.
const toWire = (f) => ({
  companyId: f.companyId,
  origId: f.origId,
  matterId: f.matterId,
  timekeeperId: f.timekeeperId,
  viewMode: f.viewMode,
  postedFilter: f.postedFilter,
  showDetails: f.showDetails,
  endDate: f.endDate,
})

/**
 * CC-49983 — casing bridge for CB property-reference metadata.
 *
 * cb-api camelizes row KEYS, but only camelizes the VALUES of an allow-listed
 * set of "this string names another field" columns — `dataField`,
 * `link*Property` (reports/routes.js `propertyRefFields`). `groups` and
 * `groupNameProperty` are NOT on that list, so they arrive PascalCase
 * ("UniqueId", "Originators", "InvoicePaymentName") while the rows they point
 * at are camelCase ("uniqueId", "originators", "invoicePaymentName"). Every
 * `row[groupKey]` lookup missed, so every row fell into one "(none)" bucket
 * and no client/timekeeper/originator identity ever rendered.
 *
 * cb-api is backend-owned (see PR #131 — unapproved cb-api edits were reverted,
 * and the deployed cb-api-qa worker wouldn't serve them anyway), so normalize
 * here and accept either casing from the server.
 */
const camelKey = (k) => (k ? String(k)[0].toLowerCase() + String(k).slice(1) : k)
const pascalKey = (k) => (k ? String(k)[0].toUpperCase() + String(k).slice(1) : k)

/**
 * Read a row value by a CB property name in EITHER casing.
 *
 * Deliberately tries both directions rather than assuming cb-api's current
 * allow-list: `groups` is PascalCase today and rows are camelCase, but the two
 * are camelized by different code paths, so pinning this to one direction is
 * how the original bug happened. Exact match wins, then camel, then Pascal.
 */
const rowValue = (row, key) => {
  if (!row || !key) return undefined
  if (row[key] !== undefined) return row[key]
  const camel = camelKey(key)
  if (row[camel] !== undefined) return row[camel]
  return row[pascalKey(key)]
}

/** Compare a CB property-name reference regardless of casing. */
const propIs = (value, name) => camelKey(value) === camelKey(name)

/**
 * Parse a CB linkable cell — turn the row's name + uniqueType into a label,
 * matching the legacy ExtJS column renderer.
 */
const formatLinkableCell = (row, col) => {
  if (!col?.linkable) return ''
  const name = rowValue(row, col.linkNameProperty) || ''
  if (!name) return 'None'
  const typeRaw = rowValue(row, col.linkTypeProperty)
  const type = typeRaw == null ? null : Number(typeRaw)
  let prefix = ''
  // CC-49983: these compared against PascalCase while cb-api had already
  // camelized linkTypeProperty, so the branch never fired and Group-by-Invoice
  // lost the Invoice:/Credit:/Payment: qualifier that distinguishes the rows.
  if ((propIs(col.linkTypeProperty, 'UniqueType') || propIs(col.linkTypeProperty, 'InvoicePaymentType')) && type) {
    // Map matches the AR controller's print formatter
    if (type === 30) prefix = 'Invoice: '
    else if (type === 47) prefix = 'Credit: '
    else if (type === 48) prefix = 'Payment: '
  }
  return `${prefix}${name}`
}

const fmtNumber = (v, decimals = 2) => {
  const n = Number(v ?? 0)
  if (!isFinite(n)) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

// Himaad UI feedback, 2026-08-27: A/R's own unpadded 'M/D/YYYY' copy is gone —
// every date a human reads here now comes from `fmtDisplayDate` (MM/DD/YYYY,
// src/utils/displayDate.js), the single Report Center display formatter. It is
// legitimate for this page to format dates at all, unlike the 119 generic
// reports: A/R SYNTHESIZES its own dataCells below (CB hands this endpoint raw
// JSON, not pre-formatted grid cells), so the display string is cb2's to choose.
// `todayStr` above is the WIRE value and stays ISO.

// displayValue / groupHeaderContent are injected as HTML by the universal
// grids (dangerouslySetInnerHTML) — escape ALL raw data placed in them.
const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

// Resolve a cell's value: normal columns read their dataField off the row;
// the aging-bucket columns (dataField "numberField" — all five share it) read
// row.additionalFields by matching the column label ({ label, value } objects).
// Pre-existing blank-buckets bug fixed in slice-5 PR1 QA.
// Aging-bucket columns don't declare dataType 'number' — treat them as numeric
// everywhere (formatting, totals, sort) alongside declared number columns.
const isNumericCol = (col) => col.dataType === 'number' || col.dataField === 'numberField'

const cellValue = (row, col) => {
  if (col.dataField === 'numberField') {
    const wanted = col.label ?? col.text
    return row.additionalFields?.find((f) => f.label === wanted)?.value
  }
  return rowValue(row, col.dataField)
}

const colDecimals = (col) =>
  parseInt(col.cellsFormatDecimals ?? col.format?.match(/N(\d)/)?.[1] ?? 2, 10)

// `reportId` is passed by the registry but unused here — AR resolves its own view
// mode from the URL and exports client-side, so it never needs the numeric id.
/*
 * Publishes AR's export view UP to the shell, from INSIDE it.
 *
 * This has to be a child component, not a hook in the page: `ReportExportViewContext`
 * is provided *by* SpecializedReportShell, and ARReport RENDERS the shell — so the
 * page sits above the provider and reads null. TrialBalanceReport's PublishedGrid
 * says the same thing in a comment; AR was the one page that never did it at all.
 *
 * What it unblocks: the shell feeds the AI panel from this ref, so without it
 * "AI Summary" on Aged A/R could only ever answer "Run the report first" on a
 * report showing 66 rows. Measured on deployed QA, report 21.
 *
 * Renders nothing — it exists purely to be inside the provider.
 */
const PublishExportView = ({ viewRef }) => {
  const exportCtx = useReportExportView()
  useEffect(() => {
    if (!exportCtx) return
    exportCtx.ref.current = () => viewRef.current?.()
    exportCtx.onPublish?.()
  }, [exportCtx, viewRef])
  return null
}

// Module-level so useLatestRun's `run` keeps one identity (spec 131). AR keeps
// its own fallback copy, exactly as before.
const runErrorMessage = (err) => err.message || 'Failed to run AR report'

const ARReport = ({ reportId, name = 'Accounts Receivable', summary, jobId }) => {
  // CC-50013 G6: document title, back navigation and the print masthead moved to
  // SpecializedReportShell along with the rest of the chrome.
  // CC-49981: PDF + masthead carry the firm's real name from system settings.
  const firmName = useFirmName()
  const { user } = useAuth0()
  const [searchParams] = useSearchParams()
  // CC-49930: page-level exports read the grid's persisted Show-$ choice.
  const prefsStore = usePreferencesStore()

  // Smart-search prefill (Layer B): open scoped to a matter/client passed via
  // ?ei=<id>&en=<name>&et=<matter|contact>. Client view when a client is passed.
  const [filters, setFilters] = useState(() => {
    const ei = searchParams.get('ei')
    const en = searchParams.get('en') || ''
    const et = searchParams.get('et')
    const base = {
      companyId: null, companyName: '',
      origId: null, origName: '',
      matterId: null, matterName: '',
      timekeeperId: null, timekeeperName: '',
      viewMode: 'Matter',
      postedFilter: null, // null | true | false
      showDetails: false,
      endDate: todayStr(),
    }
    if (ei && et === 'matter') return { ...base, matterId: ei, matterName: en }
    if (ei && et === 'contact') return { ...base, companyId: ei, companyName: en, viewMode: 'Client' }
    return base
  })
  // CC-49752 (posted-default parity): legacy defaults Invoice Posted? to
  // "Posted" when the tenant uses posting — cb2 always opened on "Don't
  // filter", so first-run totals included unposted invoices V1 hides (and
  // surfaced matters V1 doesn't). The first auto-run waits for the resolution
  // (postedDefaultReady) so the grid never shows data for a default the
  // controls don't display. Shared with Production & Origination (CC-49949)
  // via useUsePosting.
  const { ready: usePostingReady, usePosting } = useUsePosting()
  const [postedDefaultReady, setPostedDefaultReady] = useState(false)
  useEffect(() => {
    if (!usePostingReady || postedDefaultReady) return
    if (usePosting) {
      // Apply only while untouched, so a user's pre-run selection wins.
      // The Posted select derives its value from filters.postedFilter now, so
      // there is one source of truth instead of a mirrored UI state.
      setFilters((f) => (f.postedFilter === null ? { ...f, postedFilter: true } : f))
    }
    // Batched with the setFilters above, so the gated auto-run below fires in
    // the SAME commit that already carries the applied default — exactly one
    // first run, with the right payload.
    setPostedDefaultReady(true)
  }, [usePostingReady, usePosting, postedDefaultReady])
  // Spec 131: only the NEWEST run may write the rows, the spinner, the error or
  // the documents' criteria — this page re-runs on every view-mode / show-details
  // change, and none of those radios is disabled while a run is out.
  const {
    running, error: runError, ran, runKey, run: startRun,
  } = useLatestRun({ initialRunning: !!jobId, errorMessage: runErrorMessage })
  const [progress, setProgress] = useState({ loaded: 0, total: 0 })
  const [result, setResult] = useState(null)
  const [savedAsOf, setSavedAsOf] = useState(null)
  // Spec 185 (CC-53435): the reopened job's own stored params — read ONLY to state the
  // saved run's criteria on the documents. Never seeded into `filters` (a reopen must
  // not start a run, and the filter bar is not the saved run's).
  const [savedParams, setSavedParams] = useState(null)
  // Consume-once: the first run reopens the tray's saved job; later runs are fresh.
  const jobRef = useRef(jobId || null)
  // CC-49344: display filters — hide zero-balance rows (on by default) and a
  // client-side practice-area filter (By Matter view; resolved via /matters).
  const [hideZero, setHideZero] = useState(AR_DISPLAY_DEFAULTS.hideZero)
  // CC-49527/CC-49344: exclude inactive/closed matters. Data-changing
  // exclusion, so opt-in (default OFF).
  const [hideInactive, setHideInactive] = useState(AR_DISPLAY_DEFAULTS.hideInactive)
  const [practiceAreaFilter, setPracticeAreaFilter] = useState(AR_DISPLAY_DEFAULTS.practiceAreaFilter)
  const [matterPA, setMatterPA] = useState(null) // matterId -> { pa, status }; null = not loaded

  // Build the filter payload sent to the server
  const filterPayload = useMemo(() => ({
    companyId: filters.companyId,
    origId: filters.origId,
    matterId: filters.matterId,
    timekeeperId: filters.timekeeperId,
    viewMode: filters.viewMode,
    postedFilter: filters.postedFilter,
    showDetails: filters.showDetails,
    endDate: filters.endDate,
  }), [filters])

  /*
   * The document criteria — the export/PDF "Filters applied" line and the print
   * masthead's as-of line — hoisted above `runReport` (spec 131) so a run can
   * capture them when it STARTS. They used to be built inline at the shell call
   * and snapshotted by the shell's running-edge observer, which could not tell
   * two overlapping runs apart.
   *
   * Himaad UI feedback, 2026-08-27: the as-of date is FORMATTED for the reader
   * (MM/DD/YYYY). A/R is the one specialized page that bypasses
   * reportFilterSummary and interpolates a filter value straight into a
   * customer-facing export/PDF header, so this line used to read "As of
   * 2026-08-26" while the other 19 read "As of 08/26/2026". Only the READ is
   * formatted — `filters.endDate` itself stays the ISO wire value the run sends.
   * The 'today' fallback deliberately still keys off EMPTINESS, not
   * parseability: an unparseable endDate must not print a confident "As of
   * today" over a run that used something else (fmtDisplayDate returns '',
   * which is visibly missing).
   *
   * Two halves, split by spec 131. The as-of date is a RUN input, so a run
   * captures it at its start (`arRunLine`). The practice-area / zero / inactive
   * parts are DISPLAY filters — client-side, they never reach the wire (see the
   * display row below) and re-filter the SAME rows without a run — so they are
   * appended live (`arDisplayParts`), never frozen with the run: a file exported
   * after unticking "hide zero" carries the zero rows, and must not say "Zero
   * balances hidden".
   *
   * Spec 133: both halves are the BARE summary. The shell and ReportExport add
   * their own prefix — "Filters applied: " on the PDF masthead, "Filters — " on
   * the client CSV/XLSX, "Scope: " for the AI — exactly as for the other 20
   * specialized pages. AR used to hand them an already-prefixed line, so the PDF
   * read "Filters applied: Filters applied: As of …". AR's own XLSX
   * (downloadExport) keeps its single "Filters applied: " through exportMeta below.
   */
  const arRunLine = `As of ${filters.endDate ? fmtDisplayDate(filters.endDate) : 'today'}`
  const arDisplayParts = `${practiceAreaFilter ? ` · Practice area: ${practiceAreaFilter}` : ''}${hideZero ? ' · Zero balances hidden' : ''}${hideInactive ? ' · Inactive/closed matters hidden' : ''}`
  const arFiltersLine = `${arRunLine}${arDisplayParts}`
  /* A/R is a point-in-time statement; on paper the as-of date is not optional
     context, it identifies the run. Himaad UI feedback 2026-08-27: formatted for
     the reader (MM/DD/YYYY) — a printed aged-A/R statement was the last place
     still showing the raw ISO wire value. The gate stays on `filters.endDate` so
     the wire value alone decides whether a subtitle exists. */
  const arPrintSubtitle = filters.endDate ? `As of ${fmtDisplayDate(filters.endDate)}` : null

  const runReport = useCallback(() => {
    setProgress({ loaded: 0, total: 0 })
    // Spec 131: the rows are written only by the NEWEST run's commit.
    return startRun(async () => {
      const jid = jobRef.current
      jobRef.current = null // consumed — subsequent runs are fresh
      if (jid) {
        // Tray "View report" → reopen the exact saved snapshot + show its run time.
        const [st, r] = await Promise.all([reportJobService.status(jid), reportJobService.result(jid)])
        return () => { setSavedAsOf(st?.createdAt || null); setSavedParams(st?.savedParams ?? null); setResult(r?.data || null) }
      }
      // Runs as a background job (cb-api POST /reports/ar/run does the full
      // multi-step server-side), so a big AR run isn't bound to the edge timeout.
      const r = await arReportService.run(filterPayload, { reportId })
      // Spec 131 decision 3: the saved-result banner clears at the COMMIT.
      return () => { setSavedAsOf(null); setResult(r) }
    }, { criteria: { filtersSummary: arRunLine, printSubtitle: arPrintSubtitle } })
  }, [filterPayload, reportId, startRun, arRunLine, arPrintSubtitle])

  // The committed run's as-of line with TODAY's display filters on the end (see
  // `arRunLine` above) — null until a run has committed, as the shell expects.
  const ranCriteria = useMemo(
    () => (ran ? { ...ran.criteria, filtersSummary: `${ran.criteria?.filtersSummary ?? ''}${arDisplayParts}` } : null),
    [ran, arDisplayParts]
  )

  /*
   * Spec 185 (CC-53435) — a reopened job's line: ITS as-of date (savedRunCriteria.js
   * `arSavedCriteria`, the same words as `arRunLine`) with TODAY's display parts on the
   * end, exactly as `ranCriteria` does for a live run — they re-filter the saved rows on
   * screen too. Null (the shell's "not recorded") when the job carries no readable as-of
   * date, or ran scoped to an originator / client / matter / timekeeper this line
   * cannot name.
   */
  const savedRunLine = useMemo(() => {
    if (savedAsOf == null) return undefined
    const line = arSavedCriteria(savedParams)
    return line ? `${line}${arDisplayParts}` : null
  }, [savedAsOf, savedParams, arDisplayParts])
  /*
   * The line AR's OWN XLSX stamps (`exportMeta`), by the shell's precedence
   * (`documentFiltersSummary`): the saved job's line, else the constant, on a reopen;
   * the committed run's line otherwise; the panel's only before any run. This used to be
   * the PANEL's line (`arFiltersLine`) always — so a reopened A/R's own XLSX said
   * "As of <today>" over a saved run's figures, the false claim CC-53363 removed from
   * every other surface and missed here.
   */
  const arDocumentLine = savedAsOf != null
    ? (savedRunLine || SAVED_RUN_CRITERIA_UNKNOWN)
    : (ranCriteria ? ranCriteria.filtersSummary : arFiltersLine)

  // Auto-run once the posted default resolves (CC-49752) AND whenever the
  // view shape changes (view mode / show-details). CC-49355 investigation:
  // with mount-only deps, switching views presented the PREVIOUS view's rows
  // under the new view's label until Filter was clicked — stale financial
  // data under a wrong heading. Spec 131: deferred and cancellable, the shape
  // every effect-driven run on these pages now has, so an effect pass that is
  // torn down before the timer fires (a StrictMode remount) starts no run.
  useEffect(() => {
    if (!postedDefaultReady) return undefined
    const t = setTimeout(() => { runReport() }, 0)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postedDefaultReady, filters.viewMode, filters.showDetails])

  // CC-49344/CC-49527: lazily resolve matterId -> { practice area, status }
  // for the loaded result. Uses the paged /matters list (which carries
  // practiceArea + normalized status: 'active' | 'inactive' | 'closed') rather
  // than one request per matter. On failure both display filters stay hidden.
  useEffect(() => {
    if (!result?.rows?.length || filters.viewMode !== 'Matter' || matterPA) return
    let cancelled = false
    ;(async () => {
      try {
        const map = {}
        let offset = 0
        for (let page = 0; page < 10; page++) {
          const { matters, total } = await matterService.getMatters({ status: 'all', limit: 500, offset })
          for (const m of matters || []) map[String(m.id)] = { pa: m.practiceArea || '', status: m.status || '' }
          offset += (matters || []).length
          if (!matters?.length || offset >= (total || 0)) break
        }
        if (!cancelled) setMatterPA(map)
      } catch { /* non-fatal */ }
    })()
    return () => { cancelled = true }
  }, [result, filters.viewMode, matterPA])

  // Visible columns (drop hidden ones)
  const columns = useMemo(() => {
    const cols = result?.columnSettings?.columns || []
    return cols.filter((c) => {
      const hide = String(c.hide ?? 'false').toLowerCase() === 'true'
      // showDetails=false → also drop columns marked IsDetail
      if (!filters.showDetails && c.isDetail) return false
      return !hide
    })
  }, [result, filters.showDetails])

  // Group keys for grouping in the data.
  // CC-49983: normalized to camelCase — CB sends them PascalCase and cb-api
  // doesn't camelize this field's values, so the raw strings never matched a
  // row key. See camelKey/rowValue above.
  //
  // What CB sends per view mode (ARReportController.GridColumnSettings):
  //   Matter | Client | Timekeeper → []  (flat; + ["UniqueId"] with details on,
  //                                       where UniqueId IS the client/matter
  //                                       identity column, labelled per mode)
  //   GroupByOriginator            → ["Originators"]
  //   GroupByInvoice               → ["Client","MatterAttourney","InvoicePaymentName"]
  const groupKeys = useMemo(
    () => (result?.columnSettings?.groups || []).map(camelKey),
    [result]
  )

  // CC-49597: the legacy grid returns one row per invoice/payment even in
  // summary mode, so the same matter can appear several times in By-Matter
  // view. Collapse rows whose identity (group ids + every VISIBLE non-numeric
  // cell) is identical, SUMMING the numeric cells — SUM-safe because bucket
  // values are additive per invoice/payment. Details mode is untouched: one
  // row per invoice/payment is the point there.
  const dedupedRows = useMemo(() => {
    const raw = result?.rows || []
    if (!raw.length || filters.showDetails) return raw
    const textCols = columns.filter((c) => !isNumericCol(c))
    const map = new Map()
    for (const row of raw) {
      const key = [
        ...groupKeys.map((k) => String(rowValue(row, k) ?? '')),
        ...textCols.map((c) => String((c.linkable ? formatLinkableCell(row, c) : cellValue(row, c)) ?? '')),
      ].join('\u0000')
      const prev = map.get(key)
      if (!prev) {
        map.set(key, { ...row, additionalFields: (row.additionalFields || []).map((f) => ({ ...f })) })
        continue
      }
      for (const f of row.additionalFields || []) {
        const t = prev.additionalFields.find((x) => x.label === f.label)
        if (t) t.value = Number(t.value || 0) + Number(f.value || 0)
        else prev.additionalFields.push({ ...f })
      }
      for (const c of columns) {
        if (!isNumericCol(c) || c.dataField === 'numberField') continue
        prev[c.dataField] = Number(prev[c.dataField] || 0) + Number(row[c.dataField] || 0)
      }
    }
    return [...map.values()]
  }, [result, columns, groupKeys, filters.showDetails])

  // CC-49344: practice-area options actually present in this result set.
  const practiceAreaOptions = useMemo(() => {
    if (!matterPA || filters.viewMode !== 'Matter') return []
    const seen = new Set()
    for (const row of dedupedRows) {
      const pa = matterPA[String(row.matterId)]?.pa
      if (pa) seen.add(pa)
    }
    return [...seen].sort((a, b) => a.localeCompare(b))
  }, [dedupedRows, matterPA, filters.viewMode])

  // CC-49344: the rows the grid, groups and totals actually consume.
  const visibleRows = useMemo(() => {
    let rows = dedupedRows
    if (practiceAreaFilter && matterPA) {
      rows = rows.filter((r) => matterPA[String(r.matterId)]?.pa === practiceAreaFilter)
    }
    if (hideInactive && matterPA) {
      // Rows with an unknown matterId/status stay visible — never silently
      // drop rows we couldn't match against the matters lookup.
      rows = rows.filter((r) => {
        const status = matterPA[String(r.matterId)]?.status
        return !status || status === 'active'
      })
    }
    if (hideZero) {
      rows = rows.filter((r) =>
        columns.some((c) => isNumericCol(c) && Math.abs(Number(cellValue(r, c) || 0)) >= 0.005)
      )
    }
    return rows
  }, [dedupedRows, practiceAreaFilter, matterPA, hideInactive, hideZero, columns])

  // Compute totals from additionalFields (aging buckets)
  const totals = useMemo(() => {
    if (!visibleRows.length) return {}
    const out = {}
    for (const row of visibleRows) {
      for (const col of columns) {
        if (!isNumericCol(col)) continue
        const v = Number(cellValue(row, col) || 0)
        if (!isFinite(v)) continue
        const key = col.label ?? col.dataField
        out[key] = (out[key] || 0) + v
      }
    }
    return out
  }, [visibleRows, columns])

  // Subtotals per group (same math the old header chips used)
  const groupSubtotals = useCallback((rows) => {
    const out = {}
    for (const row of rows) {
      for (const col of columns) {
        if (!isNumericCol(col)) continue
        const v = Number(cellValue(row, col) || 0)
        if (!isFinite(v)) continue
        const key = col.label ?? col.dataField
        out[key] = (out[key] || 0) + v
      }
    }
    return out
  }, [columns])

  // L-1: map the AR result onto the universal grid contract
  // ({ columns, reportData, reportFooterCells }) so AR inherits column
  // show/hide, drag-reorder, resize and per-view-mode persisted layout from
  // ReportGroupedGrid / ReportDataGrid. displayValue / groupHeaderContent
  // render via dangerouslySetInnerHTML in the grids — every raw value is esc()'d.
  const gridReport = useMemo(() => {
    if (!result) return null
    // CC-49329: keep the default-sort intent (first visible text column,
    // ascending — the fix for the reverse-alpha "starts with Z" default) by
    // stamping sortDirectionName on that column; both grids seed their
    // initial sort from column metadata.
    const firstTextIdx = columns.findIndex((c) => !isNumericCol(c))
    // CC-50291: a stable per-firm rename key. `columnId` below is the POSITIONAL
    // index — it pairs a column with its dataCells and is not an identity, and
    // AR's visible set really does move (every isDetail column disappears when
    // "Show details" is off, and each of the five view modes has its own
    // shape). `dataField` is the stable name, but it is NOT unique: all five
    // aging buckets share `numberField` (measured live across all five modes),
    // so stableAliasKeys numbers repeats by OCCURRENCE — the buckets are
    // structural and always emitted Current / 30-59 / 60-89 / 90+ / Total, so
    // that ordinal holds still where a position would not.
    const aliasKeys = stableAliasKeys(columns.map((c) => c.dataField))
    const gridColumns = columns.map((c, i) => ({
      columnId: i,
      aliasKey: aliasKeys[i],
      label: c.label ?? c.text ?? c.dataField,
      visible: true,
      columnAlignment: isNumericCol(c) ? 'RIGHT' : 'LEFT',
      ...(i === firstTextIdx ? { sortDirectionName: 'ASC' } : {}),
    }))

    const toDataCells = (row) => columns.map((c, i) => {
      if (c.linkable) return { columnId: i, displayValue: esc(formatLinkableCell(row, c)) }
      const v = cellValue(row, c)
      if (c.dataType === 'date') {
        const t = Date.parse(v)
        // dataValue = timestamp so the grid sorts dates chronologically.
        return { columnId: i, displayValue: esc(fmtDisplayDate(v)), ...(Number.isFinite(t) ? { dataValue: t } : {}) }
      }
      if (isNumericCol(c)) {
        return {
          columnId: i,
          displayValue: v != null && v !== '' ? fmtNumber(v, colDecimals(c)) : '',
          // Raw number: drives numeric sort, zero-balance checks and the
          // grid's SUM-only totals guardrail.
          dataValue: Number(v || 0),
        }
      }
      return { columnId: i, displayValue: esc(v ?? '') }
    })

    // Footer cells (grand total / group subtotal) formatted like the old
    // footer row: $ + fmtNumber on numeric columns, a label in the first cell.
    const footerCellsFor = (sums, label) => columns.map((c, i) => {
      if (isNumericCol(c)) {
        const v = sums[c.label ?? c.dataField]
        return { columnId: i, displayValue: v != null ? `$${fmtNumber(v)}` : '' }
      }
      return { columnId: i, displayValue: i === 0 ? esc(label) : '' }
    })

    const reportFooterCells = Object.keys(totals).length > 0 ? footerCellsFor(totals, 'Total') : []

    // Flat mode (no server groupKeys) → the flat universal grid.
    if (!groupKeys.length) {
      return {
        columns: gridColumns,
        reportData: visibleRows.map((r) => ({ isGroup: false, dataCells: toDataCells(r) })),
        reportFooterCells,
        recordCount: visibleRows.length,
      }
    }

    // Server groupKeys → {isGroup:true} nodes.
    //
    // CC-49983: CB nests up to three levels (Group by Invoice =
    // Client › Responsible Attorney › Invoice). ReportGroupedGrid renders ONE
    // level, and this used to flatten to groupKeys[0] — so "Group by Invoice"
    // grouped by client and dropped the invoice entirely, which is exactly the
    // "no invoice detail" the tester reported. Collapse the nest to one group
    // per distinct level-TUPLE instead and show the full path in the header:
    // no level is lost, the leaf (the invoice) drives the grouping, and each
    // group still gets its own subtotal. True nested rendering is CC-49985.
    const colFor = (key) => result.columnSettings?.columns?.find((c) => propIs(c.dataField, key))
    // Label a level from CB's own group-name hint, falling back to the link
    // name (a name, not a guid) and finally the raw value.
    const levelLabel = (row, key) => {
      const col = colFor(key)
      const nameKey = col?.groupNameProperty || (col?.linkable ? col.linkNameProperty : null)
      const named = nameKey ? rowValue(row, nameKey) : undefined
      const value = named ?? rowValue(row, key)
      return value == null || value === '' ? '(none)' : String(value)
    }
    const buckets = new Map()
    for (const row of visibleRows) {
      const id = groupKeys.map((k) => String(rowValue(row, k) ?? '')).join('\u0000')
      if (!buckets.has(id)) {
        buckets.set(id, { parts: groupKeys.map((k) => levelLabel(row, k)), rows: [] })
      }
      buckets.get(id).rows.push(row)
    }
    const SEP = ' <span class="text-gray-400">&rsaquo;</span> '
    const reportData = [...buckets.values()].map(({ parts, rows }) => ({
      isGroup: true,
      groupHeaderContent:
        `${parts.map(esc).join(SEP)} <span class="text-xs font-normal text-gray-500">(${rows.length})</span>`,
      dataRows: rows.map((r) => ({ isGroup: false, dataCells: toDataCells(r) })),
      groupFooterCells: footerCellsFor(groupSubtotals(rows), 'Subtotal'),
    }))
    return { columns: gridColumns, reportData, reportFooterCells, recordCount: visibleRows.length }
  }, [result, columns, groupKeys, visibleRows, totals, groupSubtotals])

  // CC-49329 default order for exports: the first visible text column,
  // ascending — the same seed the grid starts from. The universal grid's
  // interactive sort is internal to the grid (persisted per storageKey), so
  // page-level exports use this deterministic default order.
  const defaultSortCol = useMemo(() => columns.find((c) => !isNumericCol(c)) || null, [columns])
  const sortedVisibleRows = useMemo(() => {
    if (!defaultSortCol) return visibleRows
    const val = (r) => String(
      (defaultSortCol.linkable ? formatLinkableCell(r, defaultSortCol) : cellValue(r, defaultSortCol)) ?? ''
    )
    // CC-49753: shared comparator so the export order matches the grids
    // (digits before letters, entity-decoded names in alphabetical position).
    return [...visibleRows].sort((a, b) => compareCellValues(val(a), val(b)))
  }, [visibleRows, defaultSortCol])

  // Per-view-mode layout persistence: each view mode has different columns,
  // so each gets its own stored layout slot.
  const storageKey = `report:ar:${filters.viewMode}`
  // CC-50013 G3 for the last two reports (21, 227). Joining the shell did not
  // grant saved views on its own — ReportViewsMenu is mounted per page, not by
  // the shell — so AR needs the same hook the other 16 pages use. The hook
  // carries the grid's page state with it, which is what stops a view saving
  // `pageState: null` and silently never restoring (CC-49732).
  const view = useSavedReportView(setFilters)

  // CC-49844: the mounted grid publishes its own view here (columns with the
  // user's real visibility + drag order, filtered rows, footer block). Preferred
  // over buildClientView() below, which cannot see any of that — but that stays as
  // the fallback for the beat before the grid's publish effect runs, and for a
  // render where no grid is mounted at all.
  const exportViewRef = useRef(null)

  // Shared "what you see" view for ALL client exports (PDF/CSV/XLSX): the
  // deduped + zero-hidden + practice-area-filtered rows in default sort order.
  const buildClientView = () => {
    // CC-49930: exports follow the grid's "Show $ on amounts" toggle. AR cells
    // are plain numbers (fmtNumber, no $), so the tri-state's auto/null state
    // shows no symbol — only an explicit ON may format the export as currency.
    const showCurrency = prefsStore.getCurrencySymbol?.(storageKey) === true
    const cols = columns.map((c, i) => ({
      key: `c${i}`,
      label: c.label ?? c.text ?? c.dataField,
      numeric: isNumericCol(c),
      currency: isNumericCol(c) && showCurrency,
      decimals: 2,
      summable: isNumericCol(c), // additive invoice/payment amounts (SUM-safe)
      visible: true,
      order: i,
    }))
    const rows = sortedVisibleRows.map((r) =>
      Object.fromEntries(columns.map((c, i) => [
        `c${i}`,
        c.linkable ? formatLinkableCell(r, c) : (c.dataType === 'date' ? fmtDisplayDate(cellValue(r, c)) : cellValue(r, c)),
      ]))
    )
    return { columns: cols, visibleRows: rows, allRows: rows }
  }
  const exportMeta = () => ({
    firmLine: firmName,
    title: name,
    // No subtitle (Jonathan 7/8). As-of folds into the filters block. The line
    // itself (and why its date is formatted) lives at `arFiltersLine` above,
    // hoisted by spec 131 so a run can capture it when it starts; it is the bare
    // summary (spec 133), so AR's own XLSX adds its single prefix here.
    filtersLine: `Filters applied: ${arDocumentLine}`,
    generatedLine: `Generated ${fmtDisplayDateTime(new Date())}${user?.name ? ` · Printed by ${user.name}` : ''}`,
  })

  // CC-49852 (QA rejection round 2): filenames follow V1's exact convention —
  // "{yyyyMMdd_hhmmss}_{Report_Name}.{ext}". The first fix's lowercase-hyphen
  // slug ("aged-a-r.csv") still didn't match what V1 downloads, so QA rejected
  // it against the V1 screenshot. exportFileName IS the legacy format.

  // CC-49844: PDF now goes through ReportExport, which is client-only for every
  // report and already builds its masthead from reportName/filtersSummary/firmName
  // (the same values exportMeta() supplies). CC-49594's "the PDF is what you see"
  // intent is preserved: getView() below resolves to the grid's published view.

  // CC-49354 #2: CSV/XLSX are CLIENT-built from the same view as the PDF. The
  // CB server export streams from the run filters only — it can't see the
  // client display filters (dedup / hide-zero / practice area), so its row
  // counts and totals silently diverged from the on-screen grid. All three
  // export formats now say exactly what the screen says (SUM-only totals row
  // included via buildTotalsRow, CC-49457).
  const downloadExport = async (format) => {
    try {
      const view = buildClientView()
      const scope = { honorColumns: true, honorFilters: true }
      if (format === 'csv') {
        const { exportClientCsv } = await import('@components/reports/exportClientView')
        exportClientCsv(view, scope, exportFileName(name, 'csv'))
      } else {
        const { exportClientExcel } = await import('@components/reports/exportClientView')
        await exportClientExcel(view, scope, exportFileName(name, 'xlsx'), exportMeta())
      }
    } catch (err) {
      alert(`Export failed: ${err.message}`)
    }
  }

  // CC-50013 G6: Aged A/R was the last report rendering neither through
  // ReportView nor SpecializedReportShell, which excluded it from every
  // shell-level capability at once — the reason CC-50010 and CC-50004 read as
  // regressions of fixes that had already shipped everywhere else. The page now
  // supplies its filter bar and grid to the shell and keeps its own run flow.
  //
  // What the shell now owns for AR: the back arrow + title, the print masthead,
  // the saved-result banner, the error pane (which additionally sanitises raw CB
  // exception text via userFacingError — AR printed it verbatim before), the
  // loading state, and the export toolbar.
  // CC-50013 G2 / CC-50148: shared metadata-driven bar (see buildFields).
  // View mode stays a page control in the action row: it selects a different
  // column set AND triggers its own re-run, so it is a mode, not a filter.
  const fields = useMemo(() => buildFields(filters.viewMode), [filters.viewMode])
  const filterBar = (
    <SpecializedFilterBar
      fields={fields}
      filters={filters}
      onFiltersChange={setFilters}
      defaults={makeDefaults()}
      onRun={runReport}
      isLoading={running}
      queue={{
        reportId,
        name,
        // Reuses arReportService's OWN job spec verbatim (actionKey 'ar',
        // POST /reports/ar/run-all) so a queued run and an interactive one
        // compute the same dedupe key and replay the same multi-step server path.
        actionKey: 'ar',
        endpoint: (f) => ({ method: 'POST', path: '/reports/ar/run-all', body: toWire(f) }),
        // Spec 207: an emailed run attaches CB's Excel file for the run's own filter.
        attachFormats: specializedAttachFormats('/reports/ar/run-all'),
      }}
      actions={(
        <>
          <span className="flex flex-wrap items-center gap-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            View
            {VIEW_MODES.map((m) => (
              <label key={m.id} className="flex items-center gap-1.5 text-sm font-normal normal-case cursor-pointer">
                <input
                  type="radio"
                  name="ar-view-mode"
                  value={m.id}
                  checked={filters.viewMode === m.id}
                  onChange={() => setFilters((f) => ({ ...f, viewMode: m.id, ...(m.id === 'GroupByInvoice' ? { showDetails: false } : {}) }))}
                />
                {m.label}
              </label>
            ))}
          </span>
          {/* Keyed on the view mode, and sharing the grid's storageKey: each
              AR mode renders a different column set, so a layout captured
              against Matter columns is meaningless in Timekeeper mode. */}
          <ReportViewsMenu
            key={storageKey}
            storageKey={storageKey}
            currentFilters={filters}
            onApply={view.applyView}
            getPageState={view.getPageState}
          />
        </>
      )}
    />
  )

  /*
   * Aged A/R's own instant view controls. These change what the grid shows and
   * what the client export contains WITHOUT re-running the report, which is
   * exactly what the grid's Display View panel is for — so they are handed to
   * the grid as `pageDisplayExtras` and sit under "View options" beside its own
   * toggles, instead of floating in a row of their own above the result.
   *
   * The grid's equivalents are suppressed (`showZeroToggle` /
   * `showInactiveToggle` below): AR's are authoritative because they also feed
   * dedup, the group subtotals and the client CSV/XLSX, and two checkboxes
   * that disagree about the same money is worse than one.
   *
   * VISIBILITY IS TIED TO APPLICATION, deliberately. The filters inside
   * visibleRows are gated on `matterPA` alone, with no view-mode check, and
   * matterPA is never cleared once loaded — so gating these controls on
   * `viewMode === 'Matter'` alone let a user tick one in Matter view, switch to
   * By Client, and keep a filtered A/R with no control on screen and no way to
   * undo it. Each control therefore also renders whenever its own value is
   * non-default. Do NOT "fix" this by adding the view-mode check to the filter
   * instead: that silently changes the figures for anyone who has one applied.
   */
  /*
   * spec 094 — these three are CLIENT-ONLY display filters. They never reach
   * the server (see the memo above: they filter `dedupedRows` in the browser),
   * so they are tagged as TRANSFORMS, not as filters. Counting them as filters
   * would overstate how much server-side scoping users actually do.
   *
   * The practice-area VALUE is not sent — a practice area is firm data. Only
   * whether the control is set.
   */
  const trackDisplay = (transform, detail) =>
    trackGridTransform(storageKey, transform, detail)

  /*
   * The page's half of "Reset grid layout" (E4 verify pass, 2026-08-28).
   *
   * Handed to whichever grid is mounted as `onResetPageDisplayExtras`, beside
   * `pageDisplayExtras` itself. Without it the reset was a no-op the user could
   * not see: AR suppresses the grid's own hide-zero / hide-inactive
   * (`showZeroToggle={false} showInactiveToggle={false}`), so the reset was
   * clearing invisible shadow copies while these three — the only row-hiding
   * controls actually in that menu — kept filtering the A/R grid. Report 21 is
   * the specialized baseline, so it is also the page most likely to be the one
   * someone checks the feature on.
   *
   * All three are restored, including the practice-area filter: it is the one of
   * the three that can remove rows carrying real money, and leaving it set while
   * the two checkboxes reset would be a worse half-state than resetting nothing.
   *
   * Deliberately NOT tracked as three `trackGridTransform` events. The grid
   * already emits one `reset_layout`; emitting hide_zero/hide_inactive/
   * practice_area_filter here would book three transforms the user never
   * performed and inflate spec 094's transform counts (see `trackDisplay`).
   *
   * Nothing re-runs. These are client-side filters over `dedupedRows`; the
   * server payload is untouched, so this cannot change what the report asked CB
   * for — the same promise the reset's tooltip makes.
   */
  const handleResetPageDisplayExtras = useCallback(() => {
    setHideZero(AR_DISPLAY_DEFAULTS.hideZero)
    setHideInactive(AR_DISPLAY_DEFAULTS.hideInactive)
    setPracticeAreaFilter(AR_DISPLAY_DEFAULTS.practiceAreaFilter)
  }, [])

  /*
   * spec 121 gap 2 (2026-09-09) — these two rows now use the SAME control and the
   * SAME row treatment as the grid's own toggles they sit beside.
   *
   * What they were: `<input type="checkbox" className="rounded border-gray-300
   * text-cb-accent-text focus:ring-cb-accent">` inside an `inline-flex gap-1.5`
   * label. Every one of those three utilities was INERT — Tailwind v4's preflight
   * sets `border-width: 0` on `*` so a border colour with no bare `border` width
   * paints nothing, a ring colour with no ring width emits nothing, and `text-*`
   * does not reach a native checkbox at all (only `accent-color` does). So the
   * browser drew its own default box, and a colour swap here would have been ZERO
   * pixels — which is why this is a component swap and not a token change.
   *
   * Why it was visible: these are handed to the grid as `pageDisplayExtras` and
   * render as plain siblings inside the same `flex flex-col gap-2` list as the
   * grid's converted rows (spec 117 B4/B5). So on **Aged A/R, report id 21** — the
   * specialized baseline CLAUDE.md names, i.e. the report a reviewer is most likely
   * to open — the Display View panel showed browser-default boxes eight pixels from
   * drawn ones, at `gap-1.5` against their `gap-2.5`, with no hover band and no
   * focus band. spec 117 could not fix it because ARReport.jsx was outside that
   * round's file partition; there is no partition now.
   *
   * ⚠️ NOTHING ABOUT THE BEHAVIOUR MOVES, and that is load-bearing rather than
   * incidental. Both toggles are real money controls: they filter `visibleRows`,
   * which drives the grid rows, the group buckets, `totals` AND the client export
   * (`hideZero` defaults to TRUE and REMOVES rows). They never reach the wire.
   * ControlCheckbox keeps a real `<input type="checkbox">` in the same DOM position
   * with the same `checked`/`onChange`, so `e.target.checked` is unchanged, the
   * `data-pendo` attributes still land on the input (rest-spread), and the
   * `getByRole('checkbox', { name })` reads a dozen ARReport tests use still work.
   * The render gate below (`matterPA && (viewMode === 'Matter' || hideInactive)`)
   * is untouched — see the memo above for why it must stay as it is, and do NOT
   * move that check onto the filter instead.
   */
  const pageDisplayExtras = (
    <>
      <label className={TOGGLE_ROW}>
        <ControlCheckbox
          data-pendo="report-ar-hide-zero"
          checked={hideZero}
          onChange={(e) => { trackDisplay('hide_zero', e.target.checked ? 'on' : 'off'); setHideZero(e.target.checked) }}
        />
        Hide zero balances
      </label>
      {matterPA && (filters.viewMode === 'Matter' || hideInactive) && (
        <label className={TOGGLE_ROW}>
          <ControlCheckbox
            data-pendo="report-ar-hide-inactive"
            checked={hideInactive}
            onChange={(e) => { trackDisplay('hide_inactive', e.target.checked ? 'on' : 'off'); setHideInactive(e.target.checked) }}
          />
          Hide inactive/closed matters
        </label>
      )}
      {(practiceAreaOptions.length > 0 || practiceAreaFilter) && (
        <span className={`flex items-center gap-1.5 ${TOGGLE_ROW_BAND}`}>
          <label htmlFor="ar-practice-area">Practice area</label>
          <select
            id="ar-practice-area"
            data-pendo="report-ar-practice-area"
            value={practiceAreaFilter}
            onChange={(e) => { trackDisplay('practice_area_filter', e.target.value ? 'set' : 'cleared'); setPracticeAreaFilter(e.target.value) }}
            className="min-w-0 flex-1 rounded-rc-control border border-rc-control bg-white px-2 py-1 text-xs text-rc-ink-2"
          >
            <option value="">All practice areas</option>
            {practiceAreaOptions.map((pa) => <option key={pa} value={pa}>{pa}</option>)}
            {/* A value chosen in Matter view survives a switch to a view that
                offers no options; keep it listed so the control can undo it. */}
            {practiceAreaFilter && !practiceAreaOptions.includes(practiceAreaFilter) && (
              <option value={practiceAreaFilter}>{practiceAreaFilter}</option>
            )}
          </select>
        </span>
      )}
    </>
  )

  return (
    <SpecializedReportShell
      name={name}
      summary={summary}
      running={running}
      error={runError}
      ranCriteria={ranCriteria}
      runKey={runKey}
      filterBar={filterBar}
      savedAsOf={savedAsOf}
      savedRunFiltersSummary={savedRunLine}
      /* The LIVE as-of line; the shell prints `ranCriteria`'s copy once a run
         has committed. See `arPrintSubtitle` above for why it is formatted. */
      printSubtitle={arPrintSubtitle}
      progress={progress}
      /* No result yet = nothing to export; keeps this page's original
         `!running && result` gate on the toolbar. */
      ready={!!result}
      /* CC-49844, preserved verbatim through the migration. `download` is the
         page's own full-scope client build, which stays authoritative because
         AR has no CB export endpoint that understands its client-side dedup /
         hide-zero / practice-area filters. Checking a scope box switches to the
         GRID's published view, which unlike buildClientView() knows which
         columns the user hid and in what order they dragged them. The
         buildClientView() fallback covers the beat before the grid publishes. */
      download={downloadExport}
      getView={() => exportViewRef.current?.() ?? buildClientView()}
      filtersSummary={arFiltersLine}
      hasGroups={groupKeys.length > 0}
      rowCount={sortedVisibleRows.length}
    >
      <PublishExportView viewRef={exportViewRef} />
      {/* Data grid — universal grid components (CC L-1). Column show/hide,
          drag-reorder, resize, click-to-sort and persisted layout all come
          from ReportGroupedGrid / ReportDataGrid. key= forces a remount when
          the view mode changes so each mode restores its own stored layout. */}
      {result && gridReport && (
        groupKeys.length > 0 ? (
          <ReportGroupedGrid key={`${storageKey}:${view.gridKey}`} report={gridReport} storageKey={storageKey} exportViewRef={exportViewRef} pageStateRef={view.pageStateRef} initialPageState={view.initialPageState} showZeroToggle={false} showInactiveToggle={false} pageDisplayExtras={pageDisplayExtras} onResetPageDisplayExtras={handleResetPageDisplayExtras} />
        ) : (
          <ReportDataGrid key={`${storageKey}:${view.gridKey}`} report={gridReport} storageKey={storageKey} exportViewRef={exportViewRef} pageStateRef={view.pageStateRef} initialPageState={view.initialPageState} showZeroToggle={false} showInactiveToggle={false} pageDisplayExtras={pageDisplayExtras} onResetPageDisplayExtras={handleResetPageDisplayExtras} />
        )
      )}
    </SpecializedReportShell>
  )
}

export default ARReport
